import { Uuid } from "./uuid";
import { NaiveDateTime } from "./datetime";
import { Shared, ShareSource } from "./shared";
import { AssignedExercise } from "./regimen";

export type CompletionGroupOperator = "match_all" | "match_any" | "never";
export type CompletionFieldType = "numeric" | "percent" | "duration" | "label" | "boolean" | "text" | "date" | "multi_select";
export type CompletionComparisonOperator =
  | "="
  | "!="
  | "<"
  | "<="
  | ">"
  | ">="
  | "in"
  | "contains"
  | "contains_any"
  | "contains_all";

export interface AlwaysCompletionCondition {
  type: "always";
}

export interface CompletionConditionGroup {
  type: "group";
  operator: CompletionGroupOperator;
  conditions: CompletionCondition[];
}

export interface CompletionConditionComparison {
  type: "comparison";
  field: string;
  field_type: CompletionFieldType;
  operator: CompletionComparisonOperator;
  value: string | number | boolean | Array<string | number | boolean>;
}

export interface ExerciseCompletedCountCompletionCondition {
  type: "exercise_completed_count";
  exercise_id: Uuid;
  count: number;
}

export interface StepLiveDaysCompletionCondition {
  type: "step_live_days";
  days: number;
}

export type CompletionCondition =
  | AlwaysCompletionCondition
  | CompletionConditionGroup
  | CompletionConditionComparison
  | ExerciseCompletedCountCompletionCondition
  | StepLiveDaysCompletionCondition;

export interface StepCompletionCondition {
  condition: CompletionCondition;
  target_step: TargetStep | null;
  transition_mode: TransitionMode;
}

export type TargetStep = Uuid | "complete";
export type TransitionMode = "delayed" | "immediate";

export function neverStepCompletionCondition(): StepCompletionCondition {
  return {
    condition: { type: "group", operator: "never", conditions: [] },
    target_step: null,
    transition_mode: "delayed",
  };
}

export function isNeverCompletionCondition(condition: CompletionCondition): boolean {
  return condition.type === "group" && condition.operator === "never";
}

export interface ReportingField {
  display: string;
  title?: string | Record<string, string>;
  suffix?: Record<string, string>;
  value_labels?: Record<string, Record<string, string>>;
  fraction_denominator?: string | null;
  field_type?: CompletionFieldType;
  options?: Array<{ text: string; value: string | number | boolean }>;
}

export function reportingFieldType(display: string): CompletionFieldType {
  if (display === "percent") return "percent";
  if (display.startsWith("ms_to_")) return "duration";
  if (display === "label") return "label";
  if (display === "boolean" || display === "boolean_label_only") return "boolean";
  return "numeric";
}

export function defaultComparison(field: string, reporting: ReportingField): CompletionConditionComparison {
  const field_type = reporting.field_type || reportingFieldType(reporting.display);
  const firstOption = reporting.options && reporting.options[0] ? reporting.options[0].value : "";
  return {
    type: "comparison",
    field,
    field_type,
    operator: field_type === "multi_select" ? "contains" : "=",
    value:
      field_type === "label"
        ? firstOption
        : field_type === "boolean"
          ? true
          : field_type === "multi_select"
            ? firstOption
            : field_type === "text" || field_type === "date"
              ? ""
              : 0,
  };
}

function comparisonOperators(fieldType: CompletionFieldType): CompletionComparisonOperator[] {
  if (fieldType === "boolean") return ["="];
  if (fieldType === "text") return ["=", "!="];
  if (fieldType === "label") return ["=", "!=", "in"];
  if (fieldType === "multi_select") return ["contains", "contains_any", "contains_all"];
  return ["=", "!=", "<", "<=", ">", ">="];
}

function typedLabelValue(value: string): string | number | boolean {
  if (value === "true") return true;
  if (value === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(value)) return Number(value);
  return value;
}

function reportingFieldOptions(field: ReportingField): Array<string | number | boolean> {
  if (field.options) return field.options.map((option) => option.value);
  return Object.keys(field.value_labels || {}).map(typedLabelValue);
}

function conditionFieldType(field: ReportingField): CompletionFieldType {
  return field.field_type || reportingFieldType(field.display);
}

function conditionFieldSchema(field: ReportingField): {
  field_type: CompletionFieldType;
  options: Array<string | number | boolean>;
} {
  return {
    field_type: conditionFieldType(field),
    options: reportingFieldOptions(field),
  };
}

function sameConditionFieldSchema(previous: ReportingField, next: ReportingField): boolean {
  const previousSchema = conditionFieldSchema(previous);
  const nextSchema = conditionFieldSchema(next);
  return previousSchema.field_type === nextSchema.field_type
    && JSON.stringify(previousSchema.options) === JSON.stringify(nextSchema.options);
}

function firstComparisonValue(
  value: CompletionConditionComparison["value"],
): string | number | boolean | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function comparisonValues(
  value: CompletionConditionComparison["value"],
): Array<string | number | boolean> {
  return Array.isArray(value) ? value : [value];
}

function migrateComparisonOperator(
  condition: CompletionConditionComparison,
  nextFieldType: CompletionFieldType,
): CompletionComparisonOperator {
  if (condition.field_type === "label" && nextFieldType === "multi_select") {
    if (condition.operator === "in") return "contains_any";
    return "contains";
  }
  if (condition.field_type === "multi_select" && nextFieldType === "label") {
    if (condition.operator === "contains_any" || condition.operator === "contains_all") return "in";
    return "=";
  }
  return comparisonOperators(nextFieldType).includes(condition.operator)
    ? condition.operator
    : nextFieldType === "multi_select"
      ? "contains"
      : "=";
}

function migrateComparisonValue(
  condition: CompletionConditionComparison,
  nextField: ReportingField,
  operator: CompletionComparisonOperator,
): CompletionConditionComparison["value"] {
  const fieldType = conditionFieldType(nextField);
  const defaultValue = defaultComparison(condition.field, nextField).value;
  if (fieldType === "label" || fieldType === "multi_select") {
    const available = reportingFieldOptions(nextField);
    let values = comparisonValues(condition.value);
    if (available.length) values = values.filter((value) => available.includes(value));
    if (!values.length) {
      const fallback = available[0] ?? firstComparisonValue(defaultValue);
      values = fallback === undefined ? [] : [fallback];
    }
    return ["in", "contains_any", "contains_all"].includes(operator)
      ? values
      : values[0] ?? "";
  }

  const value = firstComparisonValue(condition.value);
  if (fieldType === "boolean") {
    if (typeof value === "boolean") return value;
    if (value === "true" || value === 1) return true;
    if (value === "false" || value === 0) return false;
    return defaultValue;
  }
  if (fieldType === "numeric" || fieldType === "percent" || fieldType === "duration") {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : defaultValue;
  }
  if (typeof value === "string") return value;
  return value === undefined ? defaultValue : String(value);
}

export function migrateExerciseCompletionCondition(
  condition: CompletionCondition,
  exerciseId: Uuid,
  previousFields: Record<string, ReportingField>,
  nextFields: Record<string, ReportingField>,
): CompletionCondition | null {
  if (condition.type === "group") {
    if (condition.operator === "never") return condition;
    const conditions = condition.conditions
      .map((child) => migrateExerciseCompletionCondition(child, exerciseId, previousFields, nextFields))
      .filter((child): child is CompletionCondition => child !== null);
    return condition.conditions.length && !conditions.length
      ? null
      : { ...condition, conditions };
  }
  if (
    condition.type === "always"
    || condition.type === "step_live_days"
    || condition.type === "exercise_completed_count"
    || !condition.field.startsWith(`${exerciseId}.`)
  ) {
    return condition;
  }

  const nextField = nextFields[condition.field];
  if (!nextField) return null;
  const previousField = previousFields[condition.field];
  if (previousField && sameConditionFieldSchema(previousField, nextField)) return condition;

  const field_type = conditionFieldType(nextField);
  const operator = migrateComparisonOperator(condition, field_type);
  return {
    ...condition,
    field_type,
    operator,
    value: migrateComparisonValue(condition, nextField, operator),
  };
}

export function validateCompletionCondition(condition: CompletionCondition, root = true): boolean {
  if (condition.type === "always") return true;
  if (condition.type === "group") {
    if (condition.operator === "never") return root && condition.conditions.length === 0;
    return condition.conditions.length > 0
      && condition.conditions.every((child) => validateCompletionCondition(child, false));
  }
  if (condition.type === "exercise_completed_count") {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(condition.exercise_id)
      && Number.isInteger(condition.count)
      && condition.count >= 1;
  }
  if (condition.type === "step_live_days") {
    return Number.isInteger(condition.days) && condition.days >= 1;
  }
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/i.test(condition.field)) {
    return false;
  }
  if (condition.field_type === "boolean") {
    return condition.operator === "=" && typeof condition.value === "boolean";
  }
  if (condition.field_type === "label") {
    const isLabelValue = (value: unknown) =>
      (typeof value === "string" && value.length > 0) || typeof value === "number" || typeof value === "boolean";
    if (condition.operator === "in") {
      return Array.isArray(condition.value) && condition.value.length > 0 && condition.value.every(isLabelValue);
    }
    return (condition.operator === "=" || condition.operator === "!=") && isLabelValue(condition.value);
  }
  if (condition.field_type === "text") {
    return (
      (condition.operator === "=" || condition.operator === "!=") &&
      typeof condition.value === "string" &&
      condition.value.length > 0
    );
  }
  if (condition.field_type === "date") {
    const isIsoDate = (value: string) => {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (!match) return false;
      const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
      return date.toISOString().slice(0, 10) === value;
    };
    return (
      ["=", "!=", "<", "<=", ">", ">="].includes(condition.operator) &&
      typeof condition.value === "string" &&
      isIsoDate(condition.value)
    );
  }
  if (condition.field_type === "multi_select") {
    const isOptionValue = (value: unknown) =>
      (typeof value === "string" && value.length > 0) || typeof value === "number" || typeof value === "boolean";
    if (condition.operator === "contains") return isOptionValue(condition.value);
    return (
      (condition.operator === "contains_any" || condition.operator === "contains_all") &&
      Array.isArray(condition.value) &&
      condition.value.length > 0 &&
      condition.value.every(isOptionValue)
    );
  }
  return ["=", "!=", "<", "<=", ">", ">="].includes(condition.operator) && typeof condition.value === "number";
}

interface SurveyItem {
  id?: string;
  type?: string;
  title?: string;
  items?: Array<{ text?: string; value?: string | number | boolean }>;
}

function questionField(item: SurveyItem, exerciseTitle: string): ReportingField | null {
  if (!item.id || !/^[A-Za-z0-9_]+$/.test(item.id)) return null;
  const title = `${exerciseTitle}: ${item.title || item.id}`;
  switch (item.type) {
    case "number-field":
    case "rating":
    case "mood":
      return { display: "question_numeric", field_type: "numeric", title };
    case "select":
    case "radio-group":
      return {
        display: "question_label",
        field_type: "label",
        title,
        options: (item.items || [])
          .filter((option) => option.value !== undefined)
          .map((option) => ({ text: option.text || String(option.value), value: option.value! })),
      };
    case "checkbox":
    case "switch":
      return { display: "question_boolean", field_type: "boolean", title };
    case "checkboxes":
      return {
        display: "question_multi_select",
        field_type: "multi_select",
        title,
        options: (item.items || [])
          .filter((option) => option.value !== undefined)
          .map((option) => ({ text: option.text || String(option.value), value: option.value! })),
      };
    case "text-field":
    case "textarea":
      return { display: "question_text", field_type: "text", title };
    case "date":
    case "birthday":
      return { display: "question_date", field_type: "date", title };
    default:
      return null;
  }
}

export function questionCompletionFields(exercise: AssignedExercise, exerciseTitle: string): Record<string, ReportingField> {
  const items: SurveyItem[] =
    exercise.exercise_type === "question"
      ? exercise.question
        ? [exercise.question as SurveyItem]
        : []
      : exercise.exercise_type === "questionnaire" && exercise.questionnaire
        ? ((exercise.questionnaire.items || []) as SurveyItem[])
        : [];
  return items.reduce<Record<string, ReportingField>>((fields, item) => {
    const config = questionField(item, exerciseTitle);
    if (config && item.id) fields[qualifyCompletionField(exercise.uuid, `score.${item.id}`)] = config;
    return fields;
  }, {});
}

export function supportsTimeCompletionFields(exercise: AssignedExercise): boolean {
  return exercise.exercise_type !== "question";
}

export function supportsActiveTimeCompletionFields(exercise: AssignedExercise): boolean {
  return supportsTimeCompletionFields(exercise) && !exercise.exercise_type.startsWith("video");
}

export function isReportableExercise(exercise: AssignedExercise): boolean {
  return ["interactive", "video/builtin", "video/clinic", "assessment", "question", "questionnaire", "custom"]
    .includes(exercise.exercise_type || "interactive");
}

export function qualifyCompletionField(exerciseId: Uuid, field: string): string {
  return `${exerciseId}.${field}`;
}

export function remapCompletionCondition(
  condition: CompletionCondition,
  exerciseIdMap: Record<string, Uuid>,
): CompletionCondition {
  if (condition.type === "always") return condition;
  if (condition.type === "group") {
    return {
      ...condition,
      conditions: condition.conditions.map((child) => remapCompletionCondition(child, exerciseIdMap)),
    };
  }
  if (condition.type === "exercise_completed_count") {
    return exerciseIdMap[condition.exercise_id]
      ? { ...condition, exercise_id: exerciseIdMap[condition.exercise_id] }
      : condition;
  }
  if (condition.type === "step_live_days") return condition;
  const separator = condition.field.indexOf(".");
  if (separator === -1) return condition;
  const oldExerciseId = condition.field.slice(0, separator);
  const newExerciseId = exerciseIdMap[oldExerciseId];
  return newExerciseId
    ? { ...condition, field: `${newExerciseId}${condition.field.slice(separator)}` }
    : condition;
}

export function removeExerciseCompletionConditions(
  condition: CompletionCondition,
  exerciseId: Uuid,
): CompletionCondition | null {
  if (condition.type === "always") return condition;
  if (isNeverCompletionCondition(condition)) return condition;
  if (condition.type === "exercise_completed_count") {
    return condition.exercise_id === exerciseId ? null : condition;
  }
  if (condition.type === "step_live_days") return condition;
  if (condition.type === "comparison") {
    return condition.field.startsWith(`${exerciseId}.`) ? null : condition;
  }
  const conditions = condition.conditions
    .map((child) => removeExerciseCompletionConditions(child, exerciseId))
    .filter((child): child is CompletionCondition => child !== null);
  return conditions.length ? { ...condition, conditions } : null;
}

export function addExerciseCompletionCondition(condition: CompletionCondition, exerciseId: Uuid): CompletionCondition {
  const completed: ExerciseCompletedCountCompletionCondition = {
    type: "exercise_completed_count",
    exercise_id: exerciseId,
    count: 1,
  };
  if (isNeverCompletionCondition(condition)) return condition;
  if (condition.type === "always") {
    return { type: "group", operator: "match_all", conditions: [completed] };
  }
  if (condition.type === "group") {
    return { ...condition, conditions: [...condition.conditions, completed] };
  }
  return { type: "group", operator: "match_all", conditions: [condition, completed] };
}

export interface SequenceStep {
  id: Uuid;
  title?: string;
  exercises: AssignedExercise[];
  completion_conditions: StepCompletionCondition[];
}

export function sequenceStepTitle(step: SequenceStep, fallback: string): string {
  return step.title?.trim() || fallback;
}

export function duplicateSequenceStep(
  step: SequenceStep,
  generateUuid: () => Uuid,
  stepId = generateUuid(),
): SequenceStep {
  const copy: SequenceStep = JSON.parse(JSON.stringify(step));
  copy.id = stepId;
  const exerciseIdMap: Record<string, Uuid> = {};
  copy.exercises.forEach((exercise) => {
    const oldId = exercise.uuid;
    const newId = generateUuid();
    exerciseIdMap[oldId] = newId;
    exercise.uuid = newId;
  });
  copy.completion_conditions = copy.completion_conditions.map((completion) => ({
    ...completion,
    condition: remapCompletionCondition(completion.condition, exerciseIdMap),
  }));
  return copy;
}

export function duplicateSequenceSteps(
  steps: SequenceStep[],
  generateUuid: () => Uuid,
): SequenceStep[] {
  const stepIdMap = steps.reduce<Record<string, Uuid>>((map, step) => {
    map[step.id] = generateUuid();
    return map;
  }, {});

  return steps.map((step) => {
    const copy = duplicateSequenceStep(step, generateUuid, stepIdMap[step.id]);
    copy.completion_conditions = copy.completion_conditions.map((completion) => ({
      ...completion,
      target_step: completion.target_step && stepIdMap[completion.target_step]
        ? stepIdMap[completion.target_step]
        : completion.target_step,
    }));
    return copy;
  });
}

type Translate = (key: string, values?: Record<string, unknown>) => string;

type CompletionFieldConfig = {
  title?: unknown;
};

const builtInFieldKeys: Record<string, string> = {
  level: "exercise_level",
  level_completed: "level_completed",
  time_played: "active_time_played",
  time_total: "total_elapsed_time",
};

function localized(values: unknown, locale: string): string {
  if (!values) return "";
  if (typeof values === "string") return values;
  const translations = values as Record<string, string>;
  return translations[locale] || translations.en || "";
}

export function comparisonOperatorOptions(fieldType: CompletionFieldType, translate: Translate) {
  const operatorKeys = fieldType === "boolean"
    ? [["=", "equals"]]
    : fieldType === "text"
      ? [["=", "equals"], ["!=", "does_not_equal"]]
      : fieldType === "multi_select"
        ? [["contains", "contains"], ["contains_any", "contains_any"], ["contains_all", "contains_all"]]
        : fieldType === "label"
          ? [["=", "equals"], ["!=", "does_not_equal"], ["in", "is_any_of"]]
          : [
            ["=", "equals"],
            ["!=", "does_not_equal"],
            ["<", "less_than"],
            ["<=", "less_than_or_equal"],
            [">", "greater_than"],
            [">=", "greater_than_or_equal"],
          ];
  return operatorKeys.map(([value, key]) => ({
    text: translate(`grid_sequence_completion_condition.${key}`),
    value,
  }));
}

export function comparisonSummaryLabels(
  condition: CompletionConditionComparison,
  fields: Record<string, CompletionFieldConfig>,
  locale: string,
  translate: Translate,
) {
  const configuredTitle = localized(fields[condition.field]?.title, locale);
  const titleSeparator = configuredTitle.indexOf(": ");
  const fieldPath = condition.field.split(".").slice(1).join(".");
  const fieldKey = builtInFieldKeys[fieldPath];
  const operator = comparisonOperatorOptions(condition.field_type, translate)
    .find((option) => option.value === condition.operator);
  return {
    field: configuredTitle
      ? configuredTitle.slice(titleSeparator >= 0 ? titleSeparator + 2 : 0)
      : fieldKey
        ? translate(`grid_sequence_completion_condition.${fieldKey}`)
        : translate("grid_sequence_completion_condition.reported_value"),
    operator: operator?.text || condition.operator,
  };
}

export function sequenceExerciseTitle(
  exercise: AssignedExercise,
  exercises: Record<string, any>,
  videoExercises: Record<string, any>,
  locale: string,
  translate: Translate,
): string {
  if (exercise.title) return exercise.title;
  if (!exercise.exercise_id) return exercise.exercise_type || translate("common.exercise");
  const source = exercise.exercise_type?.startsWith("video") ? videoExercises : exercises;
  const definition = source?.[exercise.exercise_id];
  if (!definition?.title) return exercise.exercise_type || translate("common.exercise");
  return localized(definition.title, locale) || exercise.exercise_id;
}

export function completedExerciseOptions(
  step: SequenceStep,
  exercises: Record<string, any>,
  videoExercises: Record<string, any>,
  locale: string,
  translate: Translate,
) {
  return step.exercises
    .filter(isReportableExercise)
    .map((exercise, index) => ({
      text: `${sequenceExerciseTitle(exercise, exercises, videoExercises, locale, translate)} (${index + 1})`,
      value: exercise.uuid,
    }));
}

export function buildConditionFields(
  step: SequenceStep,
  exercises: Record<string, any>,
  videoExercises: Record<string, any>,
  locale: string,
  translate: Translate,
) {
  const fields: Record<string, any> = {};
  step.exercises.filter(isReportableExercise).forEach((exercise, index) => {
    const exerciseTitle = `${sequenceExerciseTitle(exercise, exercises, videoExercises, locale, translate)} (${index + 1})`;
    const definition = exercise.exercise_id ? exercises?.[exercise.exercise_id] : undefined;
    const reporting = exercise.exercise_type === "interactive" && definition ? definition.reporting || {} : {};
    Object.keys(reporting).forEach((field) => {
      const config = reporting[field];
      fields[qualifyCompletionField(exercise.uuid, `score.${field}`)] = {
        ...config,
        title: `${exerciseTitle}: ${localized(config.title, locale) || field}`,
      };
    });
    Object.assign(fields, questionCompletionFields(exercise, exerciseTitle));
    if (definition?.num_levels > 1 && !definition.level_deprecated) {
      fields[qualifyCompletionField(exercise.uuid, "level")] = {
        display: "integer",
        title: `${exerciseTitle}: ${translate("grid_sequence_completion_condition.exercise_level")}`,
      };
      fields[qualifyCompletionField(exercise.uuid, "level_completed")] = {
        display: "boolean",
        title: `${exerciseTitle}: ${translate("grid_sequence_completion_condition.level_completed")}`,
      };
    }
    if (supportsActiveTimeCompletionFields(exercise)) {
      fields[qualifyCompletionField(exercise.uuid, "time_played")] = {
        display: "ms_to_minutes_seconds",
        title: `${exerciseTitle}: ${translate("grid_sequence_completion_condition.active_time_played")}`,
      };
    }
    if (supportsTimeCompletionFields(exercise)) {
      fields[qualifyCompletionField(exercise.uuid, "time_total")] = {
        display: "ms_to_minutes_seconds",
        title: `${exerciseTitle}: ${translate("grid_sequence_completion_condition.total_elapsed_time")}`,
      };
    }
  });
  return fields;
}

export function defaultStepCondition(exercise: AssignedExercise) {
  if (!isReportableExercise(exercise)) return { type: "always" as const };
  return {
    type: "group" as const,
    operator: "match_all" as const,
    conditions: [{
      type: "exercise_completed_count" as const,
      exercise_id: exercise.uuid,
      count: 1,
    }],
  };
}

export function defaultStepCompletionCondition(exercise: AssignedExercise): StepCompletionCondition {
  return {
    condition: defaultStepCondition(exercise),
    target_step: null,
    transition_mode: "delayed",
  };
}

export function alwaysStepCompletionCondition(): StepCompletionCondition {
  return {
    condition: { type: "always" },
    target_step: null,
    transition_mode: "delayed",
  };
}

export function setStepExercises(step: SequenceStep, exerciseList: AssignedExercise[]): SequenceStep {
  return { ...step, exercises: exerciseList };
}

export function removeStep(sequence: SequenceStep[], index: number): SequenceStep[] {
  const removedId = sequence[index].id;
  return sequence
    .filter((_, stepIndex) => stepIndex !== index)
    .map((step) => {
      const completion_conditions = step.completion_conditions
        .filter((completion) => completion.target_step !== removedId);
      return {
        ...step,
        completion_conditions: completion_conditions.length
          ? completion_conditions
          : [neverStepCompletionCondition()],
      };
    });
}

export function removeExercise(
  step: SequenceStep,
  exerciseId: string,
  emptyCompletionCondition: StepCompletionCondition,
): SequenceStep {
  const exercises = step.exercises.filter((exercise) => exercise.uuid !== exerciseId);
  let completion_conditions = step.completion_conditions
    .map((completion) => ({
      ...completion,
      condition: removeExerciseCompletionConditions(completion.condition, exerciseId),
    }))
    .filter((completion) => completion.condition) as StepCompletionCondition[];
  if (!completion_conditions.length) {
    completion_conditions = [emptyCompletionCondition];
  }
  return { ...step, exercises, completion_conditions };
}

export function appendExercise(step: SequenceStep, exercise: AssignedExercise): SequenceStep {
  const completion_conditions = isReportableExercise(exercise)
    ? step.completion_conditions.map((completion) => ({
      ...completion,
      condition: addExerciseCompletionCondition(completion.condition, exercise.uuid),
    }))
    : step.completion_conditions;
  return {
    ...step,
    exercises: [...step.exercises, exercise],
    completion_conditions,
  };
}

/** Replace an exercise on a step, pruning conditions that referenced the previous exercise. */
export function replaceStepExercise(
  step: SequenceStep,
  previousExerciseId: Uuid,
  exercise: AssignedExercise,
  emptyCompletionCondition: StepCompletionCondition,
): SequenceStep {
  const withoutPrevious = removeExercise(step, previousExerciseId, emptyCompletionCondition);
  return {
    ...withoutPrevious,
    exercises: [...withoutPrevious.exercises, exercise],
  };
}

export function migrateStepExercise(
  step: SequenceStep,
  exercise: AssignedExercise,
  previousFields: Record<string, ReportingField>,
  nextFields: Record<string, ReportingField>,
  emptyCompletionCondition: StepCompletionCondition,
): SequenceStep {
  let completion_conditions = step.completion_conditions
    .map((completion) => ({
      ...completion,
      condition: migrateExerciseCompletionCondition(
        completion.condition,
        exercise.uuid,
        previousFields,
        nextFields,
      ),
    }))
    .filter((completion) => completion.condition !== null) as StepCompletionCondition[];
  if (!completion_conditions.length) completion_conditions = [emptyCompletionCondition];
  return {
    ...step,
    exercises: step.exercises.map((candidate) => candidate.uuid === exercise.uuid ? exercise : candidate),
    completion_conditions,
  };
}

export function setCompletionTarget(
  sequence: SequenceStep[],
  stepIndex: number,
  conditionIndex: number,
  target_step: string | null,
): SequenceStep[] {
  return sequence.map((step, index) => {
    if (index !== stepIndex) return step;
    const completion_conditions = step.completion_conditions.slice();
    completion_conditions.splice(conditionIndex, 1, {
      ...completion_conditions[conditionIndex],
      target_step,
    });
    return { ...step, completion_conditions };
  });
}

export function removeCompletion(sequence: SequenceStep[], stepIndex: number, conditionIndex: number): SequenceStep[] {
  return sequence.map((step, index) => {
    if (index !== stepIndex) return step;
    if (step.completion_conditions.length === 1) {
      return { ...step, completion_conditions: [neverStepCompletionCondition()] };
    }
    const completion_conditions = step.completion_conditions.slice();
    completion_conditions.splice(conditionIndex, 1);
    return { ...step, completion_conditions };
  });
}

type ConditionText = (
  condition: CompletionCondition | null | undefined,
  step: SequenceStep,
) => unknown;

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function conditionSummaryContentsHtml(
  condition: CompletionCondition | null | undefined,
  step: SequenceStep,
  conditionText: ConditionText,
  translate: Translate,
): string {
  if (condition?.type !== "group") {
    return `<span>${escapeHtml(conditionText(condition, step))}</span>`;
  }

  if (condition.operator !== "never" && condition.conditions.length === 1) {
    return conditionSummaryContentsHtml(condition.conditions[0], step, conditionText, translate);
  }

  const headingKey = condition.operator === "never"
    ? "grid_sequence_completion_condition.never_transition_description"
    : condition.operator === "match_any"
      ? "grid_sequence_summary.any_conditions_are_met"
      : "grid_sequence_summary.all_conditions_are_met";
  const heading = `<span>${escapeHtml(translate(headingKey))}</span>`;
  if (condition.operator === "never") return heading;

  const children = condition.conditions
    .map((child) => (
      `<li><div class="grid-sequence-condition-summary">${
        conditionSummaryContentsHtml(child, step, conditionText, translate)
      }</div></li>`
    ))
    .join("");
  return `${heading}<ol class="mb-0 mt-1">${children}</ol>`;
}

export function gridSequenceConditionSummaryHtml(
  condition: CompletionCondition | null | undefined,
  step: SequenceStep,
  conditionText: ConditionText,
  translate: Translate,
): string {
  return conditionSummaryContentsHtml(condition, step, conditionText, translate);
}

export default interface GridSequence {
  id: Uuid;
  created: NaiveDateTime;
  updated: NaiveDateTime;
  clinic_id: Uuid;
  title: string;
  description: string;
  instructions: string;
  sequence: SequenceStep[];
  shared: Shared | null;
  share_source: ShareSource | null;
}
